import { Navigate, useLocation, useParams } from "@tanstack/react-router";
import { useAdminWorkspace } from "@/api/admin/workspaces";
import { MerchantLogo } from "@/components/application/merchant-logo/merchant-logo";
import { TabPanel, type VerticalTab, VerticalTabs } from "@/components/application/vertical-tabs/vertical-tabs";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { AllUsersTab } from "./tabs/all-users";
import { BrandsTab } from "./tabs/brands";
import { SsoTab } from "./tabs/sso";
import { WorkspaceDetailsTab } from "./tabs/workspace-details";

const WORKSPACE_TABS: VerticalTab[] = [
  { id: "workspace-details", label: "Workspace Details" },
  { id: "brands", label: "Merchants" },
  { id: "all-users", label: "All Users" },
  { id: "sso", label: "Single Sign-On" },
];

export const WorkspaceDetailPage = () => {
  const { id } = useParams({ strict: false }) as { id: string };
  const location = useLocation();
  const { data: workspace, isLoading } = useAdminWorkspace(id);

  const activeTab = location.hash.replace(/^#/, "") || WORKSPACE_TABS[0].id;

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-tertiary">Loading...</p>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-tertiary">Workspace not found</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col mt-4 px-12">
      <div className="flex flex-row items-center gap-4">
        <MerchantLogo name={workspace.name} logoUrl={workspace.logoUrl} size="lg" />
        <div className="flex flex-col">
          <h1 className="text-xl font-semibold text-primary">{workspace.name}</h1>
          <p className="text-md text-tertiary">[ workspace ]</p>
        </div>
      </div>

      <hr className="border-tertiary mt-4 mb-8" />

      <VerticalTabs tabs={WORKSPACE_TABS} basePath={`/admin/customers/${id}`}>
        <TabPanel id="workspace-details" activeTab={activeTab}>
          <WorkspaceDetailsTab workspace={workspace} />
        </TabPanel>
        <TabPanel id="brands" activeTab={activeTab}>
          <BrandsTab workspace={workspace} />
        </TabPanel>
        <TabPanel id="all-users" activeTab={activeTab}>
          <AllUsersTab workspace={workspace} />
        </TabPanel>
        <TabPanel id="sso" activeTab={activeTab}>
          <SsoTab workspace={workspace} />
        </TabPanel>
      </VerticalTabs>
    </div>
  );
};
